import api from "../api/api";
import { useEffect, useState } from "react";
import {
  fetchAdminDashboardSummary,
  fetchAdminTopApps,
  fetchAdminTeamActivity,
  fetchProductivityTrend,
  fetchProjects,
  fetchTimelineData,
  fetchMemberActivitySummary,
  fetchWorkspaceScreenshots,
} from "../api/api";

const IDLE_THRESHOLD = 60;

export const useDashboardData = (adminId) => {
  const [workspaces, setWorkspaces] = useState([]);
  const [projects, setProjects] = useState([]);
  const [membersActivity, setMembersActivity] = useState([]);
  const [summaryCounts, setSummaryCounts] = useState({
    totalMembers: 0,
    working: 0,
    meeting: 0,
    break: 0,
    idle: 0,
    offline: 0,
  });
  const [recentEntries, setRecentEntries] = useState([]);
  const [topApps, setTopApps] = useState([]);
  const [productivityMetrics, setProductivityMetrics] = useState({ weekly: [] });
  const [activityHeatmap, setActivityHeatmap] = useState([]);
  const [screenshots, setScreenshots] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);

  const [productivityTrend, setProductivityTrend] = useState(null);
  const [productivityTimeRange, setProductivityTimeRange] = useState('monthly');

  const [timelineData, setTimelineData] = useState([]);


const [memberActivity, setMemberActivity] = useState({
  categories: [],
  active: [],
  break: [],
  idle: [],
});

const [memberActivityRange, setMemberActivityRange] = useState("daily");

useEffect(() => {
  const loadMemberActivity = async () => {
    try {
      const data = await fetchMemberActivitySummary(memberActivityRange);
      setMemberActivity({
        categories: data.categories || [],
        active: data.active || [],
        break: data.break || [],
        idle: data.idle || [],
      });
    } catch (err) {
      console.error("Failed to load member activity summary", err);
      setMemberActivity({
        categories: [],
        active: [],
        break: [],
        idle: [],
      });
    }
  };

  loadMemberActivity();
}, [memberActivityRange]);



  // ================= PRODUCTIVITY TREND =================
  useEffect(() => {
    const loadProductivityTrend = async () => {
      try {
        const data = await fetchProductivityTrend(productivityTimeRange);
        setProductivityTrend(data);
      } catch (err) {
        console.error("Failed to load productivity trend:", err);
        // Set fallback data if API fails
        setProductivityTrend({
          currentPeriod: [72, 75, 78, 80, 82, 85],
          previousPeriod: [68, 70, 72, 75, 78, 80],
          categories: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'],
          timeRange: productivityTimeRange
        });
      }
    };
    
    loadProductivityTrend();
    // Refresh every 30 seconds for real-time updates
    const interval = setInterval(loadProductivityTrend, 30000);
    return () => clearInterval(interval);
  }, [productivityTimeRange]); // Re-fetch when timeRange changes
  

  // ================= WORKSPACES & PROJECTS =================
  useEffect(() => {
    if (!adminId) return;

const loadWorkspacesAndProjects = async () => {
  try {
    // Fetch workspaces for this admin
    const wsRes = await api.get(`/workspaces?adminId=${adminId}`);

    // Ensure adminWorkspaces is an array
    const adminWorkspaces = Array.isArray(wsRes.data)
      ? wsRes.data
      : Array.isArray(wsRes.data?.workspaces)
        ? wsRes.data.workspaces
        : [];

    setWorkspaces(adminWorkspaces);

    // Fetch projects for each workspace
    const allProjects = [];
    for (const ws of adminWorkspaces) {
      try {
        const projectsData = await fetchProjects(ws.id); 
        allProjects.push(...(projectsData || []));
      } catch (err) {
        console.warn(`Failed to load projects for workspace ${ws.id}:`, err);
      }
    }

    setProjects(allProjects);
  } catch (err) {
    console.error("Failed to load workspaces/projects:", err);
  }
};

loadWorkspacesAndProjects();

  }, [adminId]);

  // ================= ADMIN SUMMARY =================
  useEffect(() => {
    const loadSummary = async () => {
      try {
        const data = await fetchAdminDashboardSummary();
        setSummaryCounts({
          totalMembers: data.totalMembers || 0,
          working: data.working || 0,
          meeting: data.inMeeting  || 0,
          break: data.breakCount || 0, // 
          offline: data.offline || 0,
        });
      } catch (err) {
        console.error("Failed to load admin dashboard summary:", err);
      }
    };
    loadSummary();
    const interval = setInterval(loadSummary, 5000);
    return () => clearInterval(interval);
  }, []);

  // ================= TOP APPS =================
  useEffect(() => {
    const loadTopApps = async () => {
      try {
        const apps = await fetchAdminTopApps();
        const formatted = (apps || [])
          .map(a => ({ name: a.name, value: Number((Number(a.value) || 0) / 3600) }))
          .filter(a => a.value > 0);
        setTopApps(formatted);
      } catch (err) {
        console.error("Failed to load admin top apps:", err);
      }
    };
    loadTopApps();
    const interval = setInterval(loadTopApps, 10000);
    return () => clearInterval(interval);
  }, []);

  // ================= TEAM ACTIVITY =================
  useEffect(() => {
    const getTodayStart = () => {
      const d = new Date();
      d.setHours(0, 0, 0, 0);
      return d;
    };

    const loadTeamActivity = async () => {
      try {
        const todayStart = getTodayStart();
        const data = await fetchAdminTeamActivity();
        const membersData = (data.entries || [])
          .filter(entry => entry.start_time && new Date(entry.start_time) >= todayStart)
          .map(entry => {
            let status = "Offline";
            if (entry.status === "working") status = "Working";
            else if (entry.status === "break") status = "Break";
            else if (entry.status === "meeting") status = "Meeting";
            else status = "Offline"; // idle is ignored
            return {
              id: entry.id,
              user_id: entry.user_id,
              name: entry.user_name,
              status,
              duration: entry.duration || 0,
              last_active_window: entry.last_active_window || "-",
              last_window_title: entry.last_window_title || "-",
              file_url: entry.file_url || null,
            };
          })
          .filter(entry => entry.status !== "Offline");
        setMembersActivity(membersData);
      } catch (err) {
        console.error("Failed to load admin team activity:", err);
      }
    };
    loadTeamActivity();
    const interval = setInterval(loadTeamActivity, 5000);
    return () => clearInterval(interval);
  }, []);

  // ================= RECENT ENTRIES =================
  useEffect(() => {
    const loadRecentEntries = async () => {
      try {
        const res = await api.get(`/time-entries/recent?limit=20`);
        const data = res.data;
        const activeEntries = (data.entries || []).filter(entry => !entry.end_time && entry.status !== 'offline');

        const mappedEntries = activeEntries.map(entry => ({
          ...entry,
          name: entry.user_name || "Unknown",
          start_time: entry.start_time,
          active_seconds: entry.active_seconds ?? entry.duration ?? 0,
          idle_seconds: entry.idle_seconds ?? 0,
          productivity_score: entry.productivity_score ?? null,
        }));

        setRecentEntries(mappedEntries);
      } catch (err) {
        console.error("Failed to fetch recent entries:", err);
      }
    };

    loadRecentEntries();
    const interval = setInterval(loadRecentEntries, 10000);
    return () => clearInterval(interval);
  }, []);


  // ================= TIMELINE DATA =================
  useEffect(() => {
    const loadTimelineData = async () => {
      try {
        const data = await fetchTimelineData(12); // Last 12 hours
        
        if (data.success && data.timelineData) {
          setTimelineData(data.timelineData);
        } else {
          console.warn("Timeline data not available or empty:", data);
          setTimelineData([]);
        }
      } catch (err) {
        console.error("Failed to load timeline data:", err);
        setTimelineData([]);
      }
    };

    loadTimelineData();
    const interval = setInterval(loadTimelineData, 15000); // Refresh every 15 seconds
    return () => clearInterval(interval);
  }, []);

  // ================= PRODUCTIVITY METRICS =================
  useEffect(() => {
    const calculateProductivityFromEntries = () => {
      if (recentEntries.length === 0) {
        setProductivityMetrics({ weekly: [] });
        return;
      }

      const days = ['Mon','Tue','Wed','Thu','Fri','Sat','Sun'];
      const productivityByDay = {};
      const teamProductivityByDay = {};
      days.forEach(day => {
        productivityByDay[day] = { total: 0, count: 0 };
        teamProductivityByDay[day] = { total: 0, count: 0 };
      });

      const last7Days = recentEntries.filter(entry => {
        const entryDate = new Date(entry.start_time);
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        return entryDate >= sevenDaysAgo;
      });

      last7Days.forEach(entry => {
        const entryDate = new Date(entry.start_time);
        const dayOfWeek = days[entryDate.getDay()];
        let productivity = entry.productivity_score;
        if (!productivity && entry.active_seconds && entry.duration) {
          const effectiveTime = Math.max(0, entry.active_seconds - (entry.idle_seconds || 0));
          productivity = Math.round((effectiveTime / entry.active_seconds) * 100);
        }

        if (productivity && productivity > 0) {
          productivityByDay[dayOfWeek].total += productivity;
          productivityByDay[dayOfWeek].count += 1;
          teamProductivityByDay[dayOfWeek].total += productivity;
          teamProductivityByDay[dayOfWeek].count += 1;
        }
      });

      const weeklyData = days.map(day => {
        const individualAvg = productivityByDay[day].count > 0
          ? Math.round(productivityByDay[day].total / productivityByDay[day].count)
          : 70 + (Math.random() * 20);
        const teamAvg = teamProductivityByDay[day].count > 0
          ? Math.round(teamProductivityByDay[day].total / teamProductivityByDay[day].count)
          : 65 + (Math.random() * 15);

        return {
          day,
          productivity: Math.min(100, individualAvg),
          teamAvg: Math.min(100, teamAvg)
        };
      });

      setProductivityMetrics({ weekly: weeklyData });
    };

    calculateProductivityFromEntries();
  }, [recentEntries]);


// ================= SCREENSHOTS =================
useEffect(() => {
  const loadScreenshots = async () => {
    try {
      if (!workspaces.length) {
        setScreenshots([]);
        return;
      }

      const results = await Promise.all(
        workspaces.map(ws => fetchWorkspaceScreenshots(ws.id))
      );

      const allScreenshots = results
        .flat()
        .sort(
          (a, b) =>
            new Date(b.created_at || b.captured_at) -
            new Date(a.created_at || a.captured_at)
        )
        .slice(0, 6);

      const formattedScreenshots = allScreenshots.map((entry, index) => ({
        id: entry.id || index,
        thumbnailUrl: entry.image,
        fullSizeUrl: entry.image,
        memberId: entry.user_id,
        memberName: entry.userName || `User ${index + 1}`,
        memberRole: "Team Member",
        memberAvatar: null,
        timestamp: entry.created_at || new Date().toISOString(),
        time: new Date(
          entry.created_at || Date.now()
        ).toLocaleTimeString([], {
          hour: "2-digit",
          minute: "2-digit"
        }),
        application: "Unknown",
        appIcon: getAppIcon("Unknown"),
        duration: "30m",
        productivityScore: 70
      }));

      setScreenshots(formattedScreenshots);

    } catch (err) {
      console.error("Failed to load screenshots:", err);
      setScreenshots([]);
    }
  };

  loadScreenshots();

  const interval = setInterval(loadScreenshots, 10000);

  return () => clearInterval(interval);
}, [workspaces]);

  // ================= TEAM MEMBERS =================
  useEffect(() => {
    const extractTeamMembersFromEntries = () => {
      const memberMap = new Map();
      recentEntries.forEach(entry=>{
        if(entry.user_id && entry.user_name && !memberMap.has(entry.user_id)) {
          memberMap.set(entry.user_id,{id:entry.user_id.toString(),name:entry.user_name,role:"Team Member",avatar:null});
        }
      });
      membersActivity.forEach(member=>{
        if(member.user_id && member.name && !memberMap.has(member.user_id)) {
          memberMap.set(member.user_id,{id:member.user_id.toString(),name:member.name,role:"Team Member",avatar:null});
        }
      });
      setTeamMembers(Array.from(memberMap.values()));
    };
    extractTeamMembersFromEntries();
  }, [recentEntries,membersActivity]);

  const getAppIcon = (appName) => {
    if(!appName) return 'bx bx-desktop';
    const appIcons = {
      'chrome':'bx bxl-chrome',
      'code':'bx bx-code-alt',
      'vscode':'bx bx-code-alt',
      'figma':'bx bxl-figma',
      'slack':'bx bxl-slack',
      'excel':'bx bxl-microsoft',
      'powerpoint':'bx bxs-slideshow',
      'word':'bx bxs-file-doc',
      'teams':'bx bxl-microsoft-teams',
      'zoom':'bx bxl-zoom',
      'whatsapp':'bx bxl-whatsapp',
      'discord':'bx bxl-discord-alt',
      'unknown':'bx bx-desktop'
    };
    const key = appName.toLowerCase();
    for(const [iconKey,iconClass] of Object.entries(appIcons)) if(key.includes(iconKey)) return iconClass;
    return 'bx bx-desktop';
  };

    const setTimeRange = (range) => {
    setProductivityTimeRange(range);
  };

  return {
    summaryCounts,
    topApps,
    recentEntries,
    membersActivity,
    productivityMetrics,
    activityHeatmap,
    screenshots,
    teamMembers,
    workspaces,
    projects,
    productivityTrend,
    timelineData, 
    setTimeRange,
    memberActivity,
    memberActivityRange,
    setMemberActivityRange,
  };
};
